"use client";

import { Fragment } from "react";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { conversationDestinationHref, type ConversationDestination } from "@/lib/conversation-target";

export interface SessionBreadcrumbSegment {
  id: string;
  title: string;
  /** Verified task route. Null marks an unavailable ancestor. */
  href?: string | null;
  /** Rich navigation destination used when no verified route is supplied. */
  destination?: ConversationDestination;
  originUnavailable?: boolean;
}

export interface SessionBreadcrumbProps {
  /** Ordered from the root session to the current session. */
  segments: readonly SessionBreadcrumbSegment[];
  /** Optional project shown before the session hierarchy. */
  projectSegment?: SessionBreadcrumbSegment;
  /**
   * Preserve space for mobile header controls by showing only the current
   * segment and an affordance for the omitted ancestors.
   */
  compact?: boolean;
  /** Embedded panels can swap/close in place instead of routing full-screen. */
  onNavigateSession?: (destination: ConversationDestination) => void;
  className?: string;
}

interface BreadcrumbItem extends SessionBreadcrumbSegment {
  isProject: boolean;
}

function BreadcrumbSeparator() {
  return <ChevronRight aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />;
}

/**
 * A session hierarchy that protects the current title from narrow headers.
 *
 * Desktop keeps short paths inline and collapses only a long middle section.
 * Compact/mobile mode always reserves the available width for the current
 * session while the ellipsis opens the complete, keyboard-accessible path.
 */
export function SessionBreadcrumb({
  segments,
  projectSegment,
  compact = false,
  onNavigateSession,
  className,
}: SessionBreadcrumbProps) {
  const { t } = useTranslation();
  const validSessionSegments = segments.filter((segment) => segment.id || segment.href === null);

  if (validSessionSegments.length === 0) return null;

  const validSegments: BreadcrumbItem[] = [
    ...(projectSegment?.id ? [{ ...projectSegment, isProject: true }] : []),
    ...validSessionSegments.map((segment) => ({ ...segment, isProject: false })),
  ];

  const currentSegment = validSegments.at(-1)!;
  const hasAncestors = validSegments.length > 1;
  const shouldCollapseMiddle = !compact && validSegments.length > 3;
  const hiddenAncestors = hasAncestors && (compact || shouldCollapseMiddle);
  const visibleAncestors = compact
    ? []
    : shouldCollapseMiddle
      ? [validSegments[0]]
      : validSegments.slice(0, -1);

  const titleFor = (segment: BreadcrumbItem) =>
    segment.title.trim()
      || t(segment.isProject ? "breadcrumb.project" : "breadcrumb.untitledSession");

  const hrefFor = (segment: BreadcrumbItem) => (
    segment.href !== undefined ? segment.href : segment.isProject
      ? `/project/${encodeURIComponent(segment.id)}`
      : conversationDestinationHref(segment.destination ?? { sessionId: segment.id }, { canonical: true })
  );

  const ancestorLink = (segment: BreadcrumbItem, compactLink = false) => (
    segment.href === null ? <span className="text-muted-foreground">{titleFor(segment)}</span> : onNavigateSession && !segment.isProject && segment.href === undefined ? (
      <button
        type="button"
        onClick={() => {
          if (segment.originUnavailable) toast.info(t("navigation.originUnavailable"));
          onNavigateSession(segment.destination ?? { sessionId: segment.id });
        }}
        className={compactLink
          ? "min-w-0 break-words text-left hover:text-foreground hover:underline"
          : "block min-w-0 max-w-40 truncate text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"}
        title={titleFor(segment)}
      >
        {titleFor(segment)}
      </button>
    ) : (
      <Link
        href={hrefFor(segment)!}
        onClick={() => {
          if (segment.originUnavailable) toast.info(t("navigation.originUnavailable"));
        }}
        className={compactLink
          ? "min-w-0 break-words hover:text-foreground hover:underline"
          : "block min-w-0 max-w-40 truncate text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"}
        title={titleFor(segment)}
      >
        {titleFor(segment)}
      </Link>
    )
  );

  return (
    <nav
      aria-label={t("breadcrumb.label")}
      className={cn("flex min-w-0 flex-1", className)}
      data-testid="session-breadcrumb"
    >
      <ol className="flex min-w-0 flex-1 items-center gap-1">
        {visibleAncestors.map((segment, index) => (
          <Fragment key={`${segment.isProject ? "project" : "session"}-${segment.id}`}>
            {index > 0 && (
              <li className="flex shrink-0 items-center">
                <BreadcrumbSeparator />
              </li>
            )}
            <li className="min-w-0 max-w-40">
              {ancestorLink(segment)}
            </li>
          </Fragment>
        ))}

        {hiddenAncestors && (
          <>
            {visibleAncestors.length > 0 && (
              <li className="flex shrink-0 items-center">
                <BreadcrumbSeparator />
              </li>
            )}
            <li className="flex shrink-0 items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0"
                    aria-label={t("breadcrumb.showFullPath")}
                    title={t("breadcrumb.showFullPath")}
                    data-testid="session-breadcrumb-expand"
                  >
                    <MoreHorizontal aria-hidden="true" className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-72"
                  aria-label={t("breadcrumb.fullPath")}
                  data-testid="session-breadcrumb-popover"
                >
                  {validSegments.map((segment, index) => {
                    const key = `${segment.isProject ? "project" : "session"}-${segment.id}`;
                    const title = titleFor(segment);
                    const label = (
                      <>
                        {index > 0 ? (
                          <ChevronRight aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
                        ) : (
                          <span aria-hidden="true" className="size-4 shrink-0" />
                        )}
                        {title}
                      </>
                    );
                    const itemClassName = "min-w-0 whitespace-normal break-words";

                    if (index === validSegments.length - 1) {
                      return (
                        <DropdownMenuItem
                          key={key}
                          disabled
                          aria-current="page"
                          textValue={title}
                          className={`${itemClassName} font-medium data-[disabled]:opacity-100`}
                        >
                          {label}
                        </DropdownMenuItem>
                      );
                    }

                    if (segment.href === null) {
                      return (
                        <DropdownMenuItem key={key} disabled textValue={title} className={`${itemClassName} text-muted-foreground`}>
                          {label}
                        </DropdownMenuItem>
                      );
                    }

                    if (onNavigateSession && !segment.isProject && segment.href === undefined) {
                      return (
                        <DropdownMenuItem key={key} asChild textValue={title} className={itemClassName}>
                          <button type="button" onClick={() => onNavigateSession(segment.destination ?? { sessionId: segment.id })} title={title}>
                            {label}
                          </button>
                        </DropdownMenuItem>
                      );
                    }

                    return (
                      <DropdownMenuItem key={key} asChild textValue={title} className={itemClassName}>
                        <Link href={hrefFor(segment)!} title={title}>
                          {label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          </>
        )}

        {shouldCollapseMiddle && (
          <>
            <li className="flex shrink-0 items-center"><BreadcrumbSeparator /></li>
            <li className="min-w-0 max-w-40">{ancestorLink(validSegments.at(-2)!)}</li>
          </>
        )}

        {(visibleAncestors.length > 0 || hiddenAncestors) && (
          <li className="flex shrink-0 items-center">
            <BreadcrumbSeparator />
          </li>
        )}
        <li
          className="min-w-0 flex-1 truncate text-sm font-semibold"
          title={titleFor(currentSegment)}
          aria-current="page"
          data-testid="session-breadcrumb-current"
        >
          {titleFor(currentSegment)}
        </li>
      </ol>
    </nav>
  );
}
